import { z } from 'zod';

/**
 * Validator for "image URLs we will store in the DB and later render in
 * <img src>". Two shapes are accepted:
 *
 *   1. `data:image/<png|jpeg|webp|gif|avif>;base64,<base64-payload>`
 *      — emitted by the in-app file picker (`fileToIconDataUrl` on the
 *      client compresses to ≤ 150 KB binary ≈ 200 KB once base64-wrapped).
 *   2. `https://...` URL — for the long-term S3/CDN-hosted path, or for
 *      external avatars proxied through the platform.
 *
 * Everything else (http:, javascript:, data:text/html, data:image/svg+xml,
 * etc.) is rejected. SVG is excluded specifically because it can carry
 * <script>, and we don't want a `data:image/svg+xml;base64,<XSS>` to ever
 * make it into a stored avatar.
 *
 * The size cap on the overall string is generous (~240 KB) — the real
 * limit is the base64 payload, and `fileToIconDataUrl` keeps that well
 * under it. Codex security review (P1 #2, 2026-05-19) flagged the old
 * `z.string().max(300_000)` for accepting anything string-shaped; this
 * validator pins both the *kind* of URL and the size.
 */

// 240 KB leaves margin over the ~200 KB the client emits and rejects
// anything an attacker would use as a denial-of-service vector
// (megabyte-sized data: URLs hammering Postgres).
const IMAGE_URL_MAX_BYTES = 240_000;

// data:image/<subtype>;base64,<payload>
//   - subtype allowlist: png, jpeg, webp, gif, avif (no svg — see comment)
//   - base64 charset only (A-Z a-z 0-9 + / =)
//   - we don't validate decoded length here, the .max() cap on the whole
//     string is the practical limit
const DATA_IMAGE_RE = /^data:image\/(png|jpeg|webp|gif|avif);base64,[A-Za-z0-9+/=]+$/;

const HTTPS_URL_RE = /^https:\/\//i;

function isAcceptableImageUrl(value: string): boolean {
  if (DATA_IMAGE_RE.test(value)) return true;
  if (HTTPS_URL_RE.test(value)) {
    // Validate it's a parseable URL so we don't store "https://[garbage".
    try {
      // eslint-disable-next-line no-new
      new URL(value);
      return true;
    } catch {
      return false;
    }
  }
  return false;
}

/**
 * Reusable Zod field for avatar / icon / banner URLs.
 *
 * Use as `imageUrlSchema` directly for required fields, or
 * `imageUrlSchema.nullable().optional()` for "leave alone / clear" PATCH
 * bodies.
 */
export const imageUrlSchema = z
  .string()
  .max(IMAGE_URL_MAX_BYTES, { message: 'image_url_too_large' })
  .refine(isAcceptableImageUrl, { message: 'image_url_invalid_scheme' });

export type ImageUrl = z.infer<typeof imageUrlSchema>;

export const IMAGE_URL_MAX = IMAGE_URL_MAX_BYTES;

// ── User-avatar bytes (P2 write-back, contract §2.2 / §2.3) ─────────────────
//
// A SEPARATE allowlist from `imageUrlSchema` above, deliberately. That schema
// is shared by group-DM icons (`routes/directMessages.ts`) and guild icons
// (`schemas/guild.ts`), which are app-owned and never leave Bunker — narrowing
// it would narrow all three. Avatars are the only images that cross to
// cispracs, so only they carry this restriction.
//
// webp and avif are excluded because Go's stdlib has no encoder for either
// (`x/image/webp` is decode-only, AVIF does not exist there), so cispracs
// cannot store them and the write-back would fail at the boundary. No
// user-visible impact: the web client canvas-re-encodes every static image to
// PNG/JPEG before upload, so it never emits webp/avif for an avatar anyway.
export const AVATAR_ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/gif'] as const;
export type AvatarMimeType = (typeof AVATAR_ALLOWED_TYPES)[number];

export function isAvatarMimeType(v: string): v is AvatarMimeType {
  return (AVATAR_ALLOWED_TYPES as readonly string[]).includes(v);
}

/**
 * Hard ceiling for avatar bytes — the same 5 MB cispracs enforces, so Bunker
 * can reject before opening an outbound connection at all.
 *
 * Note this is a ceiling, not a target: the web client compresses to ~200 KB
 * before sending, so ordinary traffic is nowhere near it.
 */
export const AVATAR_MAX_BYTES = 5 * 1024 * 1024;
